import * as sessionReplay from '@amplitude/session-replay-browser';
import * as Sentry from '@sentry/browser';

import { analytics } from './analytics';

const NETLIFY_PREVIEW_PR_REGEX = /([0-9a-z]{20,30}--|deploy-preview)/;

const isHostDisabledForTracking = (): boolean => {
  const { host } = window.location;
  return Boolean(host.match(NETLIFY_PREVIEW_PR_REGEX));
};

/**
 * Get cookie value by name
 */
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

/**
 * Get stored session ID from cookie
 */
const getStoredSessionId = (): number => {
  const sessionId = getCookie('analytics_session_id');
  return sessionId ? parseInt(sessionId, 10) : 0;
};

/**
 * Initialize Session Replay with Segment integration
 * Following Amplitude documentation: https://amplitude.com/docs/session-replay/session-replay-integration-with-segment
 */
export const initializeSessionReplay = async () => {
  const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
  const sessionReplayEnabled =
    process.env.REACT_APP_AMPLITUDE_SESSION_REPLAY_ENABLED !== 'false';
  const serverUrl = process.env.REACT_APP_AMPLITUDE_SERVER_URL;
  const isEU = serverUrl?.includes('.eu.') || !serverUrl;

  if (!apiKey || !sessionReplayEnabled) {
    return;
  }

  if (isHostDisabledForTracking()) {
    return;
  }

  try {
    const sampleRate = parseFloat(
      process.env.REACT_APP_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE || '1.0',
    );

    // Get Segment user for anonymousId (deviceId)
    // Check if analytics is properly initialized (not a stub)
    const segmentWriteKey = process.env.REACT_APP_SEGMENT_WRITE_KEY;
    if (!segmentWriteKey) {
      return;
    }

    const user = await analytics.user();
    const deviceId =
      user?.anonymousId?.() ||
      `device_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

    // Get session ID from cookie (set by Segment Amplitude destination)
    const storedSessionId = getStoredSessionId();

    // Initialize Session Replay
    await sessionReplay.init(apiKey, {
      deviceId,
      sessionId: storedSessionId || Date.now(),
      sampleRate,
      serverZone: isEU ? 'EU' : 'US',
      debugMode: process.env.NODE_ENV === 'development',
    }).promise;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('component', 'Session Replay');
      scope.setLevel('error');
      scope.setExtra('error', error);
      Sentry.captureException(error as Error);
    });
  }
};

/**
 * Identify user in Amplitude via Segment
 */
export const identifyAmplitudeUser = (
  userId: string,
  userProperties?: {
    email?: string;
    companyId?: string;
    companyName?: string;
    [key: string]: any;
  },
) => {
  try {
    analytics.identify(userId, userProperties);
  } catch (_error) {
    // Error handled silently
  }
};
